import React, { useState, useEffect } from 'react'

import TutorProfile from 'src/pages/TutorProfile/TutorProfile'
import { useMutation, useQuery } from 'react-apollo'
import {
  USER_QUERY,
  UPDATE_USER_MUTATION,
  UPDATE_USER_TUTOR_MUTATION,
} from './gql'
import TutorProfilePagePlaceholder from 'src/pages/TutorProfile/TutorProfilePagePlaceholder'

const TutorProfilePageLazy: React.FC<{
  login: string
  onShowSnackbar: (params: { message: string; type: 'error' }) => void
}> = ({ login, onShowSnackbar }) => {
  const [updateUser] = useMutation(UPDATE_USER_MUTATION)
  const [updateUserTutor] = useMutation(UPDATE_USER_TUTOR_MUTATION)

  const response = useQuery(USER_QUERY, {
    variables: {
      login,
    },
    fetchPolicy: 'network-only',
  })

  const { data, loading } = response
  type StateType = {
    originalName: string
    originalLocation: string
    originalAvatarUrl: string
    avatarUrl: string
    name: string
    location: string
    avatarFileName: string
    isEditing: boolean
    isFavorite: boolean
    isBlocked: boolean
  }

  const [state, setState] = useState<StateType>({
    originalName: null,
    originalLocation: null,
    originalAvatarUrl: null,
    avatarUrl: null,
    name: null,
    location: null,
    avatarFileName: null,
    isEditing: false,
    isFavorite: null,
    isBlocked: null,
  })

  useEffect(() => {
    if (data) {
      setState((state: StateType) => {
        return {
          ...state,
          originalName: data.profile?.name,
          originalLocation: data.profile?.location,
          originalAvatarUrl: data.profile?.avatarUrl,
          name: data.profile?.name,
          location: data.profile?.location,
          avatarFileName: null,
          isEditing: false,
          isFavorite: data.profile?.tutor?.isFavorite,
          isBlocked: data.profile?.tutor?.isBlocked,
        }
      })
    }
  }, [data])

  if (loading) {
    return <TutorProfilePagePlaceholder />
  }

  if (data) {
    const UPLOAD_LIMIT = 5000000 // 5MB
    const handleTextChange = (event: any) => {
      event.persist()
      setState((state: StateType) => {
        return {
          ...state,
          [event?.target?.name]: event?.target?.value,
        }
      })
    }

    const handleFileChange = (event: any) => {
      event.stopPropagation()
      event.preventDefault()
      const file = event.target.files[0]
      const reader = new FileReader()
      if (file?.size < UPLOAD_LIMIT && file?.type?.match(/(jpe?g|png|gif)/)) {
        reader.onload = (e) => {
          const binaryData: any = e.target.result
          const base64String = global.btoa(binaryData)
          setState((state: StateType) => {
            return {
              ...state,
              avatarFileName: file.name,
              avatarUrl: `data:${file.type};base64,${base64String}`,
            }
          })
        }
        reader.readAsBinaryString(file)
      } else {
        onShowSnackbar({ message: 'Max 5MB', type: 'error' })
      }
    }

    const handleUpdateUser = async () => {
      if (state?.name === '' || state?.location === '') {
        return
      }

      const mutation = {
        mutation: UPDATE_USER_MUTATION,
        variables: {
          name: state?.name,
          location: state?.location,
          avatarFileName: state?.avatarFileName,
          avatar: state.avatarFileName ? state?.avatarUrl : null,
        },
      }

      try {
        const updateUserResponse = await updateUser(mutation)
        if (updateUserResponse?.data?.updateUser) {
          setState((state: StateType) => {
            return {
              ...state,
              isEditing: false,
              avatarUrl: updateUserResponse?.data?.updateUser?.avatarUrl,
              avatarFileName: null,
            }
          })
        }
      } catch (e) {
        console.log(e)
      }
    }

    const handleUpdateUserTutor = async (userTutor: any) => {
      const mutation = {
        mutation: UPDATE_USER_TUTOR_MUTATION,
        variables: {
          login: data?.profile?.login,
          userTutor: userTutor,
        },
      }

      try {
        const updateUserResponse = await updateUserTutor(mutation)
        if (updateUserResponse?.data?.updateUserTutor) {
          setState((state: StateType) => {
            return {
              ...state,
              ...updateUserResponse?.data?.updateUserTutor?.tutor,
            }
          })
        }
      } catch (e) {
        console.log(e)
      }
    }

    const handleEdit = () => {
      setState((state: StateType) => {
        return {
          ...state,
          isEditing: true,
          originalName: state?.name,
          originalLocation: state?.location,
          originalAvatarUrl: state?.avatarUrl,
        }
      })
    }

    const handleCancel = () => {
      setState((state: StateType) => {
        return {
          ...state,
          isEditing: false,
          name: state?.originalName,
          location: state?.originalLocation,
          avatarUrl: state?.originalAvatarUrl,
          avatarFileName: null,
        }
      })
    }

    return (
      <TutorProfile
        // order of props is relevant as they overwrite each other.
        {...data}
        {...data?.profile}
        {...data?.profile?.tutor}
        isFavorite={data?.profile?.tutor?.isFavorite}
        isBlocked={data?.profile?.tutor?.isBlocked}
        {...state}
        onEdit={handleEdit}
        onCancel={handleCancel}
        handleUpdateUserTutor={handleUpdateUserTutor}
        handleUpdateUser={handleUpdateUser}
        handleFileChange={handleFileChange}
        handleTextChange={handleTextChange}
      />
    )
  }
}

export default TutorProfilePageLazy
